/**
 * EstimateLogPage — the Cost Breakdown entry point, now on core.estimates.
 *
 * ONE estimate registry for the whole portal: the same estimates that drive
 * MTO, bid lifecycle, and award-to-job. (The old separate cb_estimates
 * registry is gone.) Each row shows live Cost Breakdown rollups; clicking a
 * row activates that estimate for every CB sheet.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCbApi } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, HelpModal,
  LoadingState, ErrorState, EmptyState, useToast, fmt,
} from './CbUi';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import AiAccuracyCurve from './AiAccuracyCurve';
import CbAuditLogButton from './CbAuditLogButton';

const ACCENT = '#b91c1c';
const CB_AUDIT_TABLES = [
  'cb_discipline_detail', 'cb_discipline_summary', 'cb_indirects',
  'cb_site_expenses', 'cb_crew_calculation', 'cb_clarifications',
  'cb_client_wbs', 'cb_markup',
];

const STATUS_META = {
  draft:       { label: 'Draft',       color: '#6b7280', bg: '#f3f4f6' },
  in_progress: { label: 'In Progress', color: '#2563eb', bg: '#eff6ff' },
  submitted:   { label: 'Submitted',   color: '#d97706', bg: '#fffbeb' },
  awarded:     { label: 'Awarded',     color: '#059669', bg: '#ecfdf5' },
  lost:        { label: 'Lost',        color: '#dc2626', bg: '#fef2f2' },
};

const EstimateLogPage = ({ onOpenEstimate, selectedEstimate }) => {
  const api = useCbApi();
  const { showToast, ToastEl } = useToast();
  const { viewers, isConnected, connectionState } = usePresence('estimateLog', 'global', true);

  const [estimates, setEstimates] = useState([]);
  const [rollups, setRollups] = useState({});
  const [divisions, setDivisions] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [showHelp, setShowHelp] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ estimate_number: '', estimate_name: '', client_name: '', division_id: '', discipline_ids: [] });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [estRes, rollRes, divRes, discRes] = await Promise.all([
        api.get('/estimates'),
        api.get('/cost-breakdown/estimate-rollups'),
        api.get('/projects/meta/divisions').catch(() => null),
        api.get('/projects/meta/disciplines').catch(() => null),
      ]);
      setEstimates(estRes.data?.estimates || []);
      setRollups(rollRes.data?.rollups || {});
      setDivisions(divRes?.data?.divisions || divRes?.data?.data || []);
      setDisciplines(discRes?.data?.disciplines || discRes?.data?.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let out = estimates;
    if (statusFilter !== 'ALL') out = out.filter((e) => (e.status || 'draft') === statusFilter);
    if (search) {
      const term = search.toLowerCase();
      out = out.filter((e) =>
        [e.estimate_number, e.estimate_name, e.client_name, e.job_number, e.division_name]
          .some((v) => String(v ?? '').toLowerCase().includes(term))
      );
    }
    return out;
  }, [estimates, search, statusFilter]);

  const kpis = useMemo(() => {
    const byStatus = (s) => estimates.filter((e) => (e.status || 'draft') === s).length;
    return [
      { label: 'Estimates', value: estimates.length, icon: '📋', color: ACCENT },
      { label: 'In Progress', value: byStatus('in_progress') + byStatus('draft'), icon: '✏️', color: '#2563eb' },
      { label: 'Submitted', value: byStatus('submitted'), icon: '📤', color: '#d97706' },
      { label: 'Awarded', value: byStatus('awarded'), icon: '🏆', color: '#059669' },
    ];
  }, [estimates]);

  const handleCreate = useCallback(async () => {
    if (!form.estimate_number.trim() || !form.estimate_name.trim()) {
      showToast('Estimate number and name are required', 'error');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/estimates', {
        estimate_number: form.estimate_number.trim(),
        estimate_name: form.estimate_name.trim(),
        client_name: form.client_name.trim() || null,
        division_id: form.division_id || null,
        discipline_ids: form.discipline_ids,
      });
      showToast(`Estimate ${form.estimate_number} created`);
      setShowCreate(false);
      setForm({ estimate_number: '', estimate_name: '', client_name: '', division_id: '', discipline_ids: [] });
      fetchData();
      if (res.data?.estimate && onOpenEstimate) onOpenEstimate(res.data.estimate);
    } catch (err) {
      showToast(err.response?.data?.message || `Create failed: ${err.message}`, 'error');
    } finally {
      setCreating(false);
    }
  }, [api, form, fetchData, onOpenEstimate, showToast]);

  const handleStatusChange = useCallback(async (estimate, status) => {
    try {
      await api.put(`/estimates/${estimate.id}/status`, { status });
      setEstimates((prev) => prev.map((e) => (e.id === estimate.id ? { ...e, status } : e)));
      showToast(`${estimate.estimate_number} marked ${STATUS_META[status]?.label || status}`);
    } catch (err) {
      showToast(err.response?.data?.message || `Status change failed: ${err.message}`, 'error');
    }
  }, [api, showToast]);

  if (loading && estimates.length === 0) return <LoadingState label="Loading estimates…" />;
  if (error) return <ErrorState error={error} onRetry={fetchData} />;

  const cell = { padding: '9px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5, whiteSpace: 'nowrap' };

  return (
    <>
      <SheetShell
        title="📕 Estimate Log"
        subtitle="One registry for the whole portal — the same estimates drive MTO, Cost Breakdown, and award-to-job"
        gradient={`linear-gradient(135deg, ${ACCENT}, #ef4444)`}
        headerActions={
          <>
            <HeaderButton onClick={fetchData}>🔄 Refresh</HeaderButton>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={4}
              showConnectionStatus={true}
            />
            <CbAuditLogButton
              tableName={CB_AUDIT_TABLES}
              estimateId={selectedEstimate?.id || null}
              title={selectedEstimate
                ? `Audit Log — ${selectedEstimate.estimate_number}`
                : 'Estimation Audit Log (all estimates)'}
              accentColor={ACCENT}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={kpis}
        banner={(
          <div style={{ padding: '14px 32px 0', background: '#f3f4f6' }}>
            {selectedEstimate ? (
              <AiAccuracyCurve estimate={selectedEstimate} />
            ) : (
              <div style={{
                background: '#fff', border: '1px dashed #cbd5e1', borderRadius: 12,
                padding: 16, color: '#64748b', fontSize: 13.5,
              }}>
                <strong style={{ color: '#0f172a' }}>AI Accuracy Curve</strong>
                {' — '}select an estimate in the header dropdown, or click a row below, to see day-0 AI seed vs the live human package.
                You can also open <strong>Cost Assembly → AI Accuracy Curve</strong> after selecting an estimate.
              </div>
            )}
          </div>
        )}
        toolbar={
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              placeholder="Search number, name, client, job…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: '1 1 240px', maxWidth: 380, padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}
            >
              <option value="ALL">All Statuses</option>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <option key={key} value={key}>{meta.label}</option>
              ))}
            </select>
            <ActionButton color={ACCENT} onClick={() => setShowCreate(true)}>+ New Estimate</ActionButton>
          </div>
        }
      >
        {filtered.length === 0 ? (
          <EmptyState icon="📕" title={estimates.length === 0 ? 'No estimates yet' : 'No estimates match'}>
            <p>Create an estimate to start its Cost Breakdown, MTO, and bid workflow.</p>
          </EmptyState>
        ) : (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Estimate #', 'Name', 'Client', 'Division', 'Status', 'CB Hours', 'CB Cost', 'Complete', 'AI', 'SP', 'Job #', ''].map((h) => (
                    <th key={h} style={{ ...cell, fontWeight: 600, fontSize: 11.5, borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, idx) => {
                  const status = e.status || (e.awarded ? 'awarded' : 'draft');
                  const meta = STATUS_META[status] || STATUS_META.draft;
                  const roll = rollups[e.id];
                  const isActive = selectedEstimate?.id === e.id;
                  return (
                    <tr
                      key={e.id}
                      onClick={() => onOpenEstimate && onOpenEstimate(e)}
                      style={{
                        cursor: 'pointer',
                        background: isActive ? '#fef2f2' : idx % 2 === 0 ? '#fff' : '#fafbfc',
                        borderLeft: isActive ? `4px solid ${ACCENT}` : '4px solid transparent',
                      }}
                    >
                      <td style={{ ...cell, fontWeight: 700, color: ACCENT }}>{e.estimate_number}</td>
                      <td style={{ ...cell, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.estimate_name}</td>
                      <td style={cell}>{e.client_name || ''}</td>
                      <td style={cell}>{e.division_name || ''}</td>
                      <td style={cell} onClick={(ev) => ev.stopPropagation()}>
                        <select
                          value={status}
                          disabled={status === 'awarded'}
                          onChange={(ev) => handleStatusChange(e, ev.target.value)}
                          style={{
                            padding: '3px 8px', borderRadius: 12, fontSize: 11.5, fontWeight: 600,
                            color: meta.color, background: meta.bg, border: `1px solid ${meta.color}44`,
                            cursor: status === 'awarded' ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {Object.entries(STATUS_META).map(([key, m]) => (
                            <option key={key} value={key} disabled={key === 'awarded' && status !== 'awarded'}>
                              {m.label}{key === 'awarded' && status !== 'awarded' ? ' (use Award)' : ''}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ ...cell, textAlign: 'right' }}>{roll ? fmt(roll.man_hours, 'number') : ''}</td>
                      <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{roll ? fmt(roll.dollar_cost, 'currency') : ''}</td>
                      <td style={{ ...cell, textAlign: 'right' }} title="Disciplines with summary rows vs detail activity">
                        {roll
                          ? `${roll.disciplines || 0} disc${roll.detail_rows ? ` · ${roll.detail_rows} detail` : ''}`
                          : '—'}
                      </td>
                      <td style={cell} title={e.seeded_from_ai_at ? `Seeded ${e.seeded_from_ai_at}` : 'Not AI-seeded'}>
                        {e.seeded_from_ai_at ? '🌱' : '·'}
                        {e.cb_user_touched_at ? '✏️' : ''}
                      </td>
                      <td style={cell} onClick={(ev) => ev.stopPropagation()}>
                        {e.sharepoint_url ? (
                          <a href={e.sharepoint_url} target="_blank" rel="noreferrer" style={{ color: '#2563eb', fontWeight: 600 }}>↗</a>
                        ) : '·'}
                      </td>
                      <td style={cell}>{e.job_number || ''}</td>
                      <td style={{ ...cell, color: '#9ca3af', fontSize: 11 }}>{isActive ? '● active' : 'open →'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {ToastEl}
      </SheetShell>

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }} onClick={() => setShowCreate(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '92%', maxWidth: 520, maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '16px 24px', background: ACCENT, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>📕 New Estimate</h2>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#fff' }}>×</button>
            </div>
            <div style={{ padding: 24, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { key: 'estimate_number', label: 'Estimate Number *', placeholder: 'e.g. 26-LG-0071' },
                { key: 'estimate_name', label: 'Estimate Name *', placeholder: 'Project / scope name' },
                { key: 'client_name', label: 'Client', placeholder: 'Client name' },
              ].map((f) => (
                <label key={f.key} style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                  {f.label}
                  <input
                    value={form[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 5, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </label>
              ))}
              {divisions.length > 0 && (
                <label style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                  Division
                  <select
                    value={form.division_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, division_id: e.target.value }))}
                    style={{ display: 'block', width: '100%', marginTop: 5, padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}
                  >
                    <option value="">— none —</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{d.division_number ? `${d.division_number} — ` : ''}{d.division_name}</option>
                    ))}
                  </select>
                </label>
              )}
              {disciplines.length > 0 && (
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>
                  Disciplines
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {disciplines.map((d) => {
                      const on = form.discipline_ids.includes(d.id);
                      return (
                        <button
                          key={d.id}
                          onClick={() => setForm((prev) => ({
                            ...prev,
                            discipline_ids: on ? prev.discipline_ids.filter((x) => x !== d.id) : [...prev.discipline_ids, d.id],
                          }))}
                          style={{
                            padding: '5px 12px', borderRadius: 16, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                            border: on ? `2px solid ${ACCENT}` : '1px solid #d1d5db',
                            background: on ? ACCENT : '#fff', color: on ? '#fff' : '#4b5563',
                          }}
                        >
                          {d.discipline_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={{ padding: '14px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: '9px 18px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <ActionButton color={ACCENT} onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating…' : 'Create Estimate'}
              </ActionButton>
            </div>
          </div>
        </div>
      )}

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Estimate Log Help" accent={ACCENT} guideId="estimate-log-help">
        <p><strong>This is the master list of estimates</strong> — the same registry that powers the Estimates dropdown in the top bar, the MTO, and award-to-job. There is no separate Cost Breakdown registry anymore.</p>
        <h4>Working the log</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Click a row</strong> to activate that estimate — every Cost Breakdown sheet (disciplines, indirects, site expenses, one-pager) then works on it.</li>
          <li><strong>CB Hours / CB Cost</strong> are live rollups of what's been estimated so far.</li>
          <li>Change <strong>Status</strong> as the bid progresses: Draft → In Progress → Submitted → Awarded or Lost.</li>
          <li><strong>Awarding</strong> is done from Admin → Estimate Management (it creates the project and pushes the MTO into the job's tracker), so it's locked here.</li>
        </ul>
        <h4>Typical flow</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li>1. Create the estimate here (number, name, client, disciplines).</li>
          <li>2. Do the takeoff in each discipline's <strong>Detailed Estimation</strong> sheet and roll it up.</li>
          <li>3. Add <strong>Indirects</strong> and <strong>Site Expenses</strong> (import the standard rows from the libraries).</li>
          <li>4. Set <strong>Markup</strong> and read the price off the <strong>CB Summary</strong> one-pager.</li>
        </ul>
      </HelpModal>
    </>
  );
};

export default EstimateLogPage;
